import {
  ClickSource,
  CompactTypes,
  LoadingStatus,
  PanelSourceTypes,
  defaultRowHeight,
} from 'core/constants/dashboard';
import { VisualizationTypes } from 'core/constants/visualizations';
import { SelectedRow } from 'services/report.service';
import { IReportSection } from 'redux-v2/report-listing/report-listing.state';
import { APIRequestStatus } from 'core/constants/redux';
import { statusCodes } from 'core/constants/report';
import { DashboardAction } from 'redux-v2/dashboard-store/dashboard-store.state';
import { FeatureContext } from 'core/constants/common';
import {
  DrilldownConfigMapping,
  IConditionalFormatting,
  ILinkedConfig,
  ReportType as ReportBuilderType,
} from 'redux-v2/report-builder/report-builder-store.state';
import { ObjModel, ReportResponseModel } from '.';
import {
  IComboMeasureConfig, ICustomFilterDefinition, IFilterConfig, IFiltersMetaData,
} from './report-response';
import { IPaging, IReportConfig, ReportType } from './report-builder/report-builder';
import { IChartAttributes } from './report-redux';
import { Auth } from './auth-response';
import { Obj, ObjGeneric } from './obj';
import { IFilterResponse } from './filter';

export interface IDashboardItem {
  TitleSection: ITitleSection;
  Id: string;
}

export interface IDashboardList {
  loadingStatus: LoadingStatus;
  dashboardItems: Array<IDashboardItem>;
}

export interface IDashboardConfig {
  _id?: string;
  Id: string;
  CreatedBy?: string;
  TitleSection: ITitleSection;
  Type: ReportType;
  Visualization: IDashboardVisualization;
  Filters: Array<IFilterConfig>;
  Panels: Array<IPanel>;
  IsHome: boolean;
  IsPersonalized: boolean;
  IsPinned: boolean;
  FiltersMetadata?: IFiltersMetaData;
}
export interface IFilterStore {
    selectedFilter: IFilterItem,
    dynamicGlobalFilters: Array<IFilterItem>;
    staticGlobalFilters: Array<IFilterItem>;
    updatedFilterIds:Array<string>,
    animatingFilterIds:Array<string>, // Filter IDs currently showing animation (cleared after 7s)
    reportMappedFilters: IReportMappedFilters;
    updatePanelList: Array<string>,
    panelMappedPartitionColumns: IReportMappedColumns,
    latestModifiedFilterId:string,
    // Per-report custom-filter wrapper definitions, sourced from the
    // fetch-report-filters response. Used to resolve a saved constituent ID
    // array back to its wrapper ID + label at picker render time.
    reportCustomFilterDefinitions: { [reportId: string]: ICustomFilterDefinition[] };
}
export interface IFilterLinkInfo{
  // Per panel, the linked report-filter target. `string` for regular filter
  // picks (legacy + new); `string[]` only when the user picked a custom-filter
  // wrapper — the FE expands the wrapper to its constituent IDs at save time.
  [panelId: string]: string | string[]
}

export interface IFilterList {
[key: string]: IFilterItem
}
export interface IFilterItem extends IFilterConfig{
  linkedPanelFilter?: IFilterLinkInfo,
  IsApplied?: boolean,
  IsEditing?: boolean,
  hide?: boolean,
  filterData?: IFilterResponse,
  filterLoadData?:boolean,
  filterLinkedKey?: string,
  alwaysHidden?:boolean,
  delinkedFilterIds?:string[],
  Status?: string,
  ReportFilterId?: string,
  ReportLinkedTo?:string,
}
export interface ITitleSection {
  Title: string;
  Description: string;
}

export interface IDashboardVisualization {
  Margin: [number, number];
  CompactType: CompactTypes;
  RowHeight: number;
  IsDraggable: boolean;
  IsResizable: boolean;
}

export interface IPanel {
  Id: string;
  TitleSection: ITitleSection;
  Source: IPanelSource;
  Target: {
    Type: VisualizationTypes;
    GridLayout: {
      x: number;
      y: number;
      w: number;
      h: number;
      i: string;
      minW?: number;
      maxW?: number;
      minH?: number;
      maxH?: number;
      isDraggable?: boolean;
      isResizable?: boolean;
    };
    Visualization: IPanelVisualisation;
  };
  FeatureContext: FeatureContext;
  Type: ReportBuilderType
}

export interface ISource {
  SourceType: PanelSourceTypes;
  SourceId: string;
  ReportId: string;
  PageSize: number;
  Url?: string;
  SourceUrl?: string
}

export interface IPanelSource extends ISource {
  // Maps a dashboard filter ID to its linked report-filter target on this panel.
  // `string` for regular picks (legacy + new). `string[]` only when the linked
  // target is a custom-filter wrapper — the FE expands the wrapper to its
  // constituent IDs at save time, so the BE never sees a wrapper ID here.
  GlobalFiltersMapping: { [dashboardFilterId: string]: string | string[] };
}

export interface IPanelVisualisation {
  Builder: any; // an object with key as Columns and value as array of strings
  Type: VisualizationTypes;
  DisplayName?: string;
  DrilldownConfigMapping?: DrilldownConfigMapping,
  DrilldownConfigIdMapping?: DrilldownConfigMapping,
  DrilldownPagination?: IPaging,
  ConditionalFormatting?: IConditionalFormatting,
  LinkedReportsConfig?: ILinkedConfig[],
}

export interface IPanelDataResponse extends Auth {
  Raw: PanelRawData;
  Visualization: IResponseVisualizationBuilder;
}

export interface IResponseVisualizationBuilder {
  Columns?: Array<ReportResponseModel.IColumn>;
  IsRestricted?: boolean;
}

export interface PanelRawData {
  Data: Array<Object>;
  HasNextPage: boolean;
  Aggregates: IAggregatesDataDashboard;
  HasOthersColumn: boolean;
}

export interface IAggregatesDataDashboard {
  GrandTotals: Obj;
}

export interface IRequestPanelDataSuccessPayload {
  response: IPanelDataResponse | string;
  panelIds: Array<string>;
  panelStatusCode?: statusCodes;
}

export interface PanelDataFetchCriteria {
  SourceType: PanelSourceTypes;
  SourceId: string;
  Filters: any;
  PageSize: number;
  Url: string;
  Target?:Array<string>;
  FiltersMetadata?:ObjModel.Obj;
  FeatureContext?: FeatureContext;
  SourceUrl?: string;
}

export interface IPanelPayloads {
  Target: Array<string>;
  Source: PanelDataFetchCriteria;
  MappingFilters: Object;
  isFiltered: boolean;
  FiltersApplied: string[];
  FiltersMetaData?:ObjModel.Obj;
}

export interface IFiltersOnchange {
  FilterID: string;
  FilterValue: any;
}

export interface SaveTempDrilldownCriteria {
  SourceId: string;
  DataSourceId: string;
  DataSourceType: PanelSourceTypes;
  Filters: any;
  SelectedMeasure: string;
  SelectedRow: Array<SelectedRow>;
  MeasureFieldAlias?: string;
  DrilldownOnRowTotal?: boolean;
  FiltersMetadata?: ReportResponseModel.IFiltersMetaData;
  linkedReportId?: string;
  LinkedFilters?: any;
  LinkedReportsConfig?: ILinkedConfig[];
}

export interface ISaveTempDrilldownSuccessPayload {
  panelId: string;
  ddStateId: string;
}

export enum DashboardActionTypes {
  CopyEmbedURL = 'Copy_Embed_URL',
}

export interface IDashboardData {
  Raw: PanelRawData;
  Visualization: IResponseVisualizationBuilder;
  loadingState: LoadingStatus;
  saveTempViewLoadingStatus: LoadingStatus;
  saveTempDrilldownLoadingStatus: LoadingStatus;
  ddStateId: string;
  linkedReportId?: string;
  panelStatusCode: statusCodes;
}

// Dashboard Builder Interface
export interface IDashboardBuilder {
  dashboardBuilderInfo: IDashboardBuilderInfo;
  requestProcessing: IRequestProcessing;
  assortedInfo: IAssortedDashboardInfo;
  selectedReportWidget: IReportWidget;
  embedURLSettings: IEmbedURLSettings;
}

export interface IEmbedURLSettings {
  title?: string;
  decodedUrl: string;
  source: ClickSource;
  sourceUrl: string;
  isUrlError: boolean;
  isMailMergeError: boolean;
}

export interface IReportWidget {
  rawReportConfig?: IReportConfig;
  reportData?: IPanelDataResponse;
  mappedMetaFields?: object;
  widgetSettings?: IWidgetSettings;
}

export interface IDashboardRequestProcessing {
  [DashboardAction.DELETE_DASHBOARD]: APIRequestStatus;
  [DashboardAction.GET_FILTERS_LIST_REQUEST]: APIRequestStatus;
  [DashboardAction.REQUEST_DASHBOARDS_LIST]:APIRequestStatus;
  [DashboardAction.SAVE_TEMP_VIEW_REQUEST]:APIRequestStatus;
  [DashboardAction.SAVE_TEMP_DRILLDOWN_REQUEST]:APIRequestStatus;
  [DashboardAction.SET_PERSONALIZED_DASHBOARD_REQUEST]:APIRequestStatus;
  [DashboardAction.DASHBOARD_ACCESS_CONTROL_REQUEST]: APIRequestStatus;
  [DashboardAction.REQUEST_DASHBOARD_CONFIG]:APIRequestStatus;
}
export interface IRequestProcessing {
  [DashboardAction.GET_REPORT_LIST_REQUEST]: APIRequestStatus;
  [DashboardAction.GET_REPORT_INFO]: APIRequestStatus;
  [DashboardAction.SAVE_DASHBOARD_CONFIG]: APIRequestStatus;
  [DashboardAction.GET_SELECTED_REPORT_WIDGET_INFO]: APIRequestStatus;
  [DashboardAction.REQUEST_DASHBOARD_CONFIG]: APIRequestStatus;
  [DashboardAction.VALIDATE_EMBED_URL]: APIRequestStatus;
  [DashboardAction.FILTER_DATA_LOAD_REQUEST]: ObjGeneric<APIRequestStatus>;
  [DashboardAction.GET_WIDGET_REPORT_DATA_REQUEST]:APIRequestStatus;
}

export interface IDashboardBuilderInfo {
  reportList: Array<IReportSection>;
  reportConfig: IReportConfig;
}

export interface IAssortedDashboardInfo {
  reportSearchKey: string;
  isFormDisabled: boolean;
  dashboardId: string;
  context: DashboardContext;
  addedWidget: string;
  widgetEditInfo: IWidgetEditInfo;
}

export interface IWidgetEditInfo{
  id: string;
  widgetDetails?: ObjModel.Obj;
}
export const defaultDashboardConfig: IDashboardConfig = {
  Id: '',
  TitleSection: {
    Title: '',
    Description: '',
  },
  Type: ReportType.Custom,
  Panels: [],
  Filters: [],
  Visualization: {
    IsDraggable: true,
    IsResizable: true,
    Margin: [20, 20],
    RowHeight: defaultRowHeight,
    CompactType: CompactTypes.Vertical,
  },
  IsHome: false,
  IsPersonalized: false,
  IsPinned: false,
};

export enum DashboardContext {
  Empty = '',
  Dashboard = 'Dashboard',
  DashboardBuilder = 'DashboardBuilder',
}

export interface IWidgetSettings {
  Type?: VisualizationTypes;
  Title?: string;
  PageSize?: number;
  attributes?: IChartAttributes;
  uniquePanelId?: any; // while adding to dashboard config we use this
  Columns?: Array<string |IWidgetColumnInfo>;
  SelectedReportId?: string;
  SelectedMeasures?: Array< string | IWidgetColumnInfo>;
  ComboConfig?: IComboMeasureConfig[];
  XAxisColumns?: string[];
  YAxisColumns?: string[];
}

export interface IDashboardPanelPayload {
  reportConfig: IReportConfig;
  widgetSettings: IWidgetSettings;
  isPanelEdit?: boolean;
}

export interface IReportMappedFilters {
  [key: string]: IFilterConfig[]
}

export interface IFilterListResponse {
  ReportFilters: {
    [key: string]: {
      Filters: IFilterConfig[],
      PartitionColumnIds:string[],
      CustomFilterDefinitions?: ICustomFilterDefinition[],
      // Sub-builder filters that are constituents of a custom filter on this report. Each
      // carries its QueryId. Grouped by QueryId into the SubBuilders shape and fed to
      // buildCustomFiltersFromDefinitions so custom filters with sub-query constituents
      // resolve their schema in the dashboard linking pool.
      CustomFilterSubBuilderFilters?: IFilterConfig[],
    }
  }
}

export interface IReportMappedColumns {
[key: string]: {
  isPartitioned: boolean,
  partitionFilterIds: string[],
}
}

export enum HomeDashboardPosition {
  CenterAligned = -170,
  LeftAligned = -290
}

export interface IBatchRequestPayload{
  target: string[],
  status: statusCodes
}

export interface IWidgetColumnInfo {
    Id: string,
    Name: string,
}
